import * as React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import {
  ReadonlyURLSearchParams,
  useParams,
  usePathname,
  useSearchParams,
} from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useUserStore as useRestaurantProfileStore } from '@/stores/restuarant/user';
import { useUserStore } from '@/stores/customer/user';
import useStore from '@/stores/useStore';
import LocationSelector from './LocationSelector';
import { UserSignIn } from '@/components/ui/Icons';
import HeaderNavLinks from './HeaderNavLinks';
import HeaderProfile from './HeaderProfile';
import HeaderProfileRestaurant from './HeaderProfileRestaurant';
import { BRAND_LOGO_ALT_TEXT } from '@/constant/configs';
import { storeFront, restaurant } from '@/lib/utils/routeLinks';
import HeadlessModal from '@/components/shared/HeadlessModal';
import LanguageToggle from './LanguageToggle';
import TagsDropdown from './TagsDropdown';
import CityDropdown from './CityDropdown';
import { cn } from '@/lib/utils';

interface ModalMenuProps {
  openDrawerMenu: boolean;
  handleMenuClose: () => void;
  showVendorMenu?: boolean;
  showNavSearch?: boolean;
}

const ModalMenu = ({
  openDrawerMenu,
  handleMenuClose,
  showVendorMenu = true,
  showNavSearch = true,
}: ModalMenuProps) => {
  const [loading, setLoading] = React.useState<boolean>(true);
  const params = useParams();
  const pathname = usePathname();
  const user = useUserStore((state) => state.user);
  const restaurantProfile = useStore(
    useRestaurantProfileStore,
    (state) => state.user
  );

  const t = useTranslations('');

  const locale: string = params.locale as string;
  const [lang] = locale.split('-');

  React.useEffect(() => {
    setLoading(false);
  }, []);

  const vendorPages = [
    storeFront.forVendor.en(),
    storeFront.forVendor.de(),
    storeFront.forCompany.en(),
    storeFront.forCompany.de(),
  ];
  const isVendorPage = vendorPages.some((page) => pathname.includes(page));
  const loginPath = isVendorPage
    ? restaurant.login.de()
    : storeFront.login.en();
  const isHomePage = pathname === `/${locale}`;
  const isCompanyPage = [
    storeFront.forCompany.de(),
    storeFront.forCompany.en(),
  ].some((route) => pathname === `/${locale}${route}`);

  //= Get Query Params values
  const searchParams: ReadonlyURLSearchParams = useSearchParams();
  const pageType: string | null = searchParams.get('type');
  const lat: string | null = searchParams.get('lat');
  const lng: string | null = searchParams.get('lng');
  const restaurantSearchPathName = `${pathname}?lat=${lat}&lng=${lng}&type=${pageType}`;

  return (
    <>
      <HeadlessModal
        isOpen={openDrawerMenu}
        closeModal={handleMenuClose}
        dialogBodyClass="overflow-y-auto"
      >
        <div className="relative pb-48">
          <div className="">
            <div className="flex items-center justify-between px-5 pb-5">
              <div className="flex items-center justify-between">
                <Link href={'/'} className="">
                  <Image
                    src="/logo.svg"
                    alt={BRAND_LOGO_ALT_TEXT}
                    width={230}
                    height={41}
                  />
                </Link>
              </div>
            </div>
          </div>
          <div className="mt-5 space-y-5 px-5">
            <div className="location-selector-mobile">
              {restaurantSearchPathName ===
              `/${
                params.locale
              }${storeFront.restaurants.de()}?lat=${lat}&lng=${lng}&type=delivery` ? (
                <LocationSelector
                  pathName={restaurantSearchPathName}
                  lat={lat}
                  lng={lng}
                  pageType={pageType}
                  rootClasses="block"
                />
              ) : null}

              {restaurantSearchPathName ===
              `/${
                params.locale
              }${storeFront.restaurants.de()}?lat=${lat}&lng=${lng}&type=pickup` ? (
                <LocationSelector
                  pathName={restaurantSearchPathName}
                  lat={lat}
                  lng={lng}
                />
              ) : null}
            </div>

            {user ? (
              <HeaderProfile closeMenu={handleMenuClose} />
            ) : restaurantProfile ? (
              <HeaderProfileRestaurant />
            ) : showVendorMenu ? (
              <div className="flex flex-col space-y-4 text-paragraph">
                <HeaderNavLinks />
              </div>
            ) : null}

            <div className="space-y-4">
              {/* <HeaderStorefrontLinks closeDrawer={handleMenuClose} /> */}
              {!isCompanyPage && (
                <div className="flex flex-col space-y-8 xl:hidden">
                  {/* <HeaderOnlineShopSearch /> */}
                  <div className="-ml-3 space-y-4">
                    <TagsDropdown />
                    <CityDropdown />
                  </div>
                </div>
              )}

              {/* New "So funktioniert's" Link */}
              {/* {isHomePage && (
                <Link
                  href="#howItWorks"
                  className="flex items-center gap-1 text-gray-900 hover:text-black"
                  onClick={(e) => {
                    e.preventDefault();
                    document
                      .getElementById('howItWorks')
                      ?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  <div className="">
                    <QuestionMarkCircleIcon className="w-5" />
                  </div>
                  <div>{t('common.how_it_works')}</div>
                </Link>
              )} */}
            </div>
          </div>
        </div>
        <div className="absolute bottom-[25%] left-0 right-0 flex w-full items-center justify-center px-10">
          {loading ? (
            <div className="flex h-[50px] w-full items-center justify-start">
              <div className="h-[41px] w-[41px] shrink-0 animate-pulse rounded-full bg-slate-600"></div>
            </div>
          ) : (
            <>
              <div className="mt-10 flex w-full items-center justify-between space-x-5">
                {!user && !restaurantProfile && (
                  <Link
                    href={loginPath}
                    className={cn(
                      'flex items-center justify-center rounded-[30px] bg-black px-[15px] py-[13px] text-sm font-normal text-white transition-colors hover:bg-primary-700 hover:text-white'
                    )}
                  >
                    {isVendorPage ? t('restaurant_login') : t('sign_in')}{' '}
                    <UserSignIn className="ml-[11.85px]" />
                  </Link>
                )}
                <LanguageToggle />
              </div>
            </>
          )}
        </div>
      </HeadlessModal>
    </>
  );
};

export default ModalMenu;
